import { useEffect, useState } from 'react'
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router'

import type { OrdersSearch } from './index'
import { useOrderWorkflowTracking } from '@/hooks/use-order-workflow-tracking'
import { useAuth } from '@/hooks/use-auth'
import { PageBackButton } from '@/components/common/PageBackButton'
import { SalesOrderForm } from '@/components/forms/sales-order-form'
import { MobileSalesOrderForm } from '@/components/orders/mobile-sales-order-form'
import { LIST_SEARCH_KEYS, recallListSearch } from '@/lib/list-search-store'
import { m } from '@/lib/paraglide/messages'

interface NewOrderSearch {
    customer_id?: string
    customer_name?: string
    customer_phone?: string
}

export const Route = createFileRoute('/_app/_authed/_sidebar/orders/new')({
    component: NewOrderPage,
    validateSearch: (search: Record<string, unknown>): NewOrderSearch => ({
        customer_id: (search.customer_id as string) || undefined,
        customer_name: (search.customer_name as string) || undefined,
        customer_phone: (search.customer_phone as string) || undefined,
    }),
})

export function NewOrderPage() {
    const navigate = useNavigate()
    const { customer_id, customer_name, customer_phone } = Route.useSearch()

    // Stable mobile detection via state — avoids React hooks ordering violation.
    // Initialised synchronously from matchMedia so there's no layout flash.
    const [isMobile, setIsMobile] = useState(
        () =>
            typeof window !== 'undefined' &&
            window.matchMedia('(max-width: 767px)').matches,
    )

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 767px)')
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
        mq.addEventListener('change', handler)
        return () => mq.removeEventListener('change', handler)
    }, [])

    const router = useRouter()
    const { user, impersonating } = useAuth()
    const workflow = useOrderWorkflowTracking(
        {
            actorId: impersonating?.actor.id ?? user?.id ?? '',
            impersonating: Boolean(impersonating),
            identityKey: `${user?.id ?? ''}:${impersonating?.actor.id ?? ''}`,
        },
        isMobile ? 'mobile' : 'desktop',
        Boolean(customer_id),
    )
    useEffect(
        () =>
            router.subscribe('onBeforeNavigate', ({ toLocation }) => {
                if (toLocation.pathname !== '/orders/new')
                    workflow.finish('navigated_away')
            }),
        [router, workflow],
    )

    // Tạo xong thì về danh sách sạch để thấy ngay đơn vừa lên; huỷ thì trả
    // người dùng về đúng bộ lọc/trang họ đang xem trước khi bấm "Tạo đơn".
    const handleSuccess = () => void navigate({ to: '/orders', search: {} })
    const handleCancel = () => {
        workflow.finish('closed')
        void navigate({
            to: '/orders',
            search:
                recallListSearch<OrdersSearch>(LIST_SEARCH_KEYS.orders) ?? {},
        })
    }

    if (isMobile) {
        return (
            <>
                {/* Sticky header — mobile only */}
                <div className="sticky top-0 z-10 flex items-center gap-3 border-b bg-background px-4 py-3">
                    <PageBackButton onBack={handleCancel} className="ml-0" />
                    <h1 className="flex-1 text-base font-semibold">
                        {m.order_new_title()}
                    </h1>
                </div>

                <MobileSalesOrderForm
                    defaultCustomerId={customer_id}
                    workflow={workflow}
                    onSuccess={handleSuccess}
                    onCancel={handleCancel}
                />
            </>
        )
    }

    return (
        <div className="space-y-5 page-pad">
            <div className="flex items-center gap-3">
                <PageBackButton onBack={handleCancel} />
                <h1 className="text-xl font-semibold tracking-tight">
                    {m.order_new_title()}
                </h1>
            </div>
            <SalesOrderForm
                defaultCustomerId={customer_id}
                defaultCustomerName={customer_name}
                defaultCustomerPhone={customer_phone}
                workflow={workflow}
                onSuccess={handleSuccess}
                onCancel={handleCancel}
            />
        </div>
    )
}
